import {
    Bullhorn,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    ChevronRight,
    CicsSystemGroup,
    Dashboard,
    InformationSquare,
    Rocket,
    Settings,
} from '@carbon/icons-react';
import { Fade, Tooltip } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { env } from '../../env';
import { useStream } from '../../hooks/useStream';
import { EditorModeEnum, RoleEnum, StageViewModeEnum } from '../../models/Index';
import StateService from '../../services/stateService/StateService';
import { useStyles } from './styles';
import { Collapse } from 'react-collapse';

type Props = {
    propWidth?: string;
};

const Navigation = ({ propWidth = '220px' }: Props) => {
    const [currentRoles, setCurrentRoles] = useStream(StateService.currentRoles$);
    const [rolesPresent, setRolesPresent] = useState(false);
    const [navState, setNavState] = useStream(StateService.openNavigation$);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [showCheckTel, setShowCheckTel] = useState(false);
    const [showCheckMail, setShowCheckMail] = useState(false);
    const [isNavOpen, setIsNavOpen] = useStream(StateService.openNavigation$);
    const classes = useStyles();
    const [t] = useTranslation();
    let includesCustomerLabel = false;
    let includesStageGrouping = false;
    const [isNavigationCollapsed, setIsNavigationCollapsed] = useState(() => {
        const storedIsCollapsed = localStorage.getItem('isNavigationCollapsed');
        // if found, parse it to boolean and return, otherwise default to true
        return storedIsCollapsed !== null ? storedIsCollapsed === 'true' : true;
    });
    const [isCollapsed, setIsCollapsed] = useState(false);
    const collapsableId = 3;

    const mainMenu = [
        {
            id: 0,
            link: () => openInternalLink('/'),
            name: 'COMMON.UNIFIED_DASHBOARD',
            icon: Dashboard,
            subItems: [],
        },
        {
            id: 1,
            link: () => openInternalLink('/dashboard-grouping'),
            name: 'COMMON.STAGE_GROUPING_LABEL',
            icon: CicsSystemGroup,
            subItems: [],
        },
        {
            id: 2,
            link: () => openInternalLink('/customer-settings'),
            name: 'COMMON.CUSTOMER_SETTINGS_LABEL',
            icon: Settings,
            subItems: [],
        },
        {
            id: 3,
            link: () => openExternalLink('/help'),
            name: 'COMMON.ONLINE_HELP_LABEL',
            icon: InformationSquare,
            subItems: [],
        },
        // stays as an example of how to use sub entries in navigation
        // {
        //     id: 3,
        //     link: () => openExternalLink('/online-help'),
        //     name: 'COMMON.ONLINE_HELP_LABEL',
        //     icon: InformationSquare,
        //     subItems: [
        //         {
        //             id: 31,
        //             name: 'COMMON.ONLINE_HELP_GENERAL_LABEL',
        //             icon: InformationSquare,
        //             link: () => openExternalLink('/online-help'),
        //         },
        //         {
        //             id: 32,
        //             name: 'COMMON.ONLINE_HELP_RELEASE_NOTES_LABEL',
        //             icon: Bullhorn,
        //             link: () => openExternalLink('/release-notes'),
        //         },
        //     ],
        // },
        {
            id: 4,
            link: () => openExternalLink(getCockpitLink()),
            name: 'NAVIGATION.COCKPIT_LABEL',
            icon: Rocket,
            subItems: [],
        },
    ];

    const handleToggleCollapse = (menuItemId) => {
        if (menuItemId === collapsableId) {
            setIsCollapsed((prevIsCollapsed) => !prevIsCollapsed);
        }
    };

    const openExternalLink = (adress: string) => {
        window.open(adress, '_blank');
    };

    const openInternalLink = (adress: string, event?) => {
        event?.preventDefault();
        StateService.editorMode$.next(EditorModeEnum.CLOSED);
        StateService.selectedRoute$.next(adress);
    };

    const getCockpitLink = () => {
        return env.REACT_APP_COCKPIT_URL;
    };

    const isLinkActive = (link: any) => {
        const currentPath = window.location.pathname;
        const regex = /['"]([^'"]+)['"]/;
        const match = regex.exec(link.toString());
        const linkValue = match ? match[1] : '';

        return currentPath === linkValue;
    };

    const handleContactClick = () => {
        setIsContactOpen((prevState) => !prevState);
    };

    const handleCopyToClipboardTel = () => {
        const telLink = document.querySelector('.clickables__tel a');
        const telNumber = telLink.textContent;
        navigator.clipboard.writeText(telNumber);
        setShowCheckTel(true);

        // Wait for a second and then change the icon back to the original
        setTimeout(() => {
            setShowCheckTel(false);
        }, 1000);
    };

    const handleCopyToClipboardMail = () => {
        const mailLink = document.querySelector('.clickables__mail a');
        const mail = mailLink.textContent;
        navigator.clipboard.writeText(mail);
        setShowCheckMail(true);

        // Wait for a second and then change the icon back to the original
        setTimeout(() => {
            setShowCheckMail(false);
        }, 1000);
    };

    const handleNavigationLinkClick = (event, item) => {
        event.preventDefault(); // Prevent the default link behavior
        item.link(event); // Execute the item's link function
        StateService.stageViewMode$.next(StageViewModeEnum.PUBLISHED);
    };

    const handleToggleNavigation = () => {
        setIsNavigationCollapsed((prev) => !prev);
        setIsNavOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };

        // Add event listener for window resize
        window.addEventListener('resize', handleResize);

        // Cleanup on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // for storing 'isNavigationCollapsed' state changes to localStorage
    useEffect(() => {
        localStorage.setItem('isNavigationCollapsed', isNavigationCollapsed.toString());
    }, [isNavigationCollapsed]);

    return (
        <>
            <CssBaseline />
            {/*desktop*/}
            <div className={!navState && viewportWidth < 768 ? classes.hide : ''}>
                <div
                    className={isNavigationCollapsed ? classes.collapsedNav : classes.navigation}
                    style={{
                        width: isNavigationCollapsed ? '48px' : `${propWidth}`,
                        minWidth: isNavigationCollapsed ? '48px' : `${propWidth}`,
                        display: currentRoles && [RoleEnum.VIEWER].includes(currentRoles[0]) ? 'none' : 'flex',
                    }}
                    data-testid="navigation"
                >
                    <div className="links">
                        <ul className={classes.linksUl}>
                            {mainMenu &&
                                mainMenu.map((menu) => (
                                    <div key={menu.id}>
                                        {menu.subItems && menu.subItems.length > 0 ? (
                                            <div>
                                                {isNavigationCollapsed ? (
                                                    <Tooltip
                                                        classes={{
                                                            tooltip: classes.tooltip,
                                                        }}
                                                        title={t(menu.name)}
                                                        placement="left-start"
                                                        TransitionComponent={Fade}
                                                        arrow
                                                    >
                                                        <li
                                                            key={menu.id}
                                                            className={`${classes.linkElem} ${isLinkActive(menu.link) ? classes.active : ''
                                                                }`}
                                                            onClick={() => handleToggleCollapse(menu.id)}
                                                        >
                                                            <a href="#" className={classes.link}>
                                                                {menu.icon && <menu.icon />}
                                                                {!isNavigationCollapsed && <span>{t(menu.name)}</span>}
                                                            </a>
                                                        </li>
                                                    </Tooltip>
                                                ) : (
                                                    <li
                                                        key={menu.id}
                                                        className={`${classes.linkElem} ${isLinkActive(menu.link) ? classes.active : ''}`}
                                                        onClick={() => handleToggleCollapse(menu.id)}
                                                    >
                                                        <a href="#" className={classes.link}>
                                                            {menu.icon && <menu.icon />}
                                                            {!isNavigationCollapsed && (
                                                                <div className={classes.collapseNameWrapper}>
                                                                    <span>{t(menu.name)}</span>
                                                                    {!isCollapsed ? (
                                                                        <span className={classes.chevron}>
                                                                            <ChevronDown />
                                                                        </span>
                                                                    ) : (
                                                                        <span className={classes.chevron}>
                                                                            <ChevronUp />
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </a>
                                                    </li>
                                                )}
                                                <Collapse isOpened={isCollapsed}>
                                                    {menu.subItems.map((subItem) => (
                                                        <div key={subItem.id}>
                                                            {isNavigationCollapsed ? (
                                                                <Tooltip
                                                                    classes={{
                                                                        tooltip: classes.tooltip,
                                                                    }}
                                                                    title={t(subItem.name)}
                                                                    placement="left-start"
                                                                    TransitionComponent={Fade}
                                                                    arrow
                                                                >
                                                                    <li
                                                                        key={subItem.id}
                                                                        className={`${classes.subItemElem} ${isLinkActive(subItem.link) ? classes.active : ''
                                                                            }`}
                                                                    >
                                                                        <a
                                                                            href="#"
                                                                            className={classes.link}
                                                                            onClick={() => handleNavigationLinkClick(event, subItem)}
                                                                        >
                                                                            {subItem.icon && <subItem.icon />}
                                                                            {!isNavigationCollapsed && <span>{t(subItem.name)}</span>}
                                                                        </a>
                                                                    </li>
                                                                </Tooltip>
                                                            ) : (
                                                                <>
                                                                    <li
                                                                        key={subItem.id}
                                                                        className={`${classes.linkElem} ${isLinkActive(subItem.link) ? classes.active : ''
                                                                            }`}
                                                                    >
                                                                        <a
                                                                            href="#"
                                                                            className={classes.link}
                                                                            onClick={() => handleNavigationLinkClick(event, subItem)}
                                                                        >
                                                                            {subItem.icon && <subItem.icon />}
                                                                            {!isNavigationCollapsed && <span>{t(subItem.name)}</span>}
                                                                        </a>
                                                                    </li>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </Collapse>
                                            </div>
                                        ) : (
                                            <>
                                                {isNavigationCollapsed ? (
                                                    <Tooltip
                                                        classes={{
                                                            tooltip: classes.tooltip,
                                                        }}
                                                        title={t(menu.name)}
                                                        placement="left-start"
                                                        TransitionComponent={Fade}
                                                        arrow
                                                    >
                                                        <li
                                                            key={menu.id}
                                                            className={`${classes.linkElem} ${isLinkActive(menu.link) ? classes.active : ''
                                                                }`}
                                                        >
                                                            <a
                                                                href="#"
                                                                className={classes.link}
                                                                onClick={() => handleNavigationLinkClick(event, menu)}
                                                            >
                                                                {menu.icon && <menu.icon />}
                                                                {!isNavigationCollapsed && <span>{t(menu.name)}</span>}
                                                            </a>
                                                        </li>
                                                    </Tooltip>
                                                ) : (
                                                    <li
                                                        key={menu.id}
                                                        className={`${classes.linkElem} ${isLinkActive(menu.link) ? classes.active : ''}`}
                                                    >
                                                        <a
                                                            href="#"
                                                            className={classes.link}
                                                            onClick={() => handleNavigationLinkClick(event, menu)}
                                                        >
                                                            {menu.icon && <menu.icon />}
                                                            {!isNavigationCollapsed && <span>{t(menu.name)}</span>}
                                                        </a>
                                                    </li>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))}
                        </ul>
                    </div>
                    <div className={classes.collapsableBtnWrapper}>
                        <div className={classes.collapsableBtn} onClick={handleToggleNavigation}>
                            {isNavigationCollapsed ? <ChevronRight /> : <ChevronLeft />}
                        </div>
                    </div>
                    {/*uncomment when reseller logic is set*/}
                    {/*<div className={classes.contact} onClick={handleContactClick}>
                                <Chat />
                                <span style={{ lineHeight: 1 }}>{t('NAVIGATION.CONTACT')}</span>
                                <Dialog
                                    open={isContactOpen}
                                    maxWidth="md"
                                    classes={{ paper: classes.dialogWrapper }}
                                    BackdropProps={{ invisible: true }}
                                >
                                    <DialogContent onClick={(e) => e.stopPropagation()}>
                                        <div className={classes.description}>
                                            <Chat />
                                            <span className={classes.description__text}>{t('NAVIGATION.CONTACT_DESCRIPTION')}</span>
                                        </div>
                                        <Typography variant="h3" style={{ fontSize: '16px' }}>
                                            {t('NAVIGATION.CUSTOMER_SERVICE_DESK')}
                                        </Typography>
                                        <div className={classes.clickables}>
                                            <div className="clickables__tel">
                                                <Phone />
                                                <a href={t('NAVIGATION.CONTACT_TEL_ANCHOR')}>{t('NAVIGATION.CONTACT_TEL')}</a>
                                            </div>
                                            <div className="copy">
                                                <Tooltip
                                                    classes={{
                                                        tooltip: classes.tooltip,
                                                    }}
                                                    title={t('COMMON.COPY_LABEL')}
                                                    placement="right-start"
                                                    TransitionComponent={Fade}
                                                    data-testid="copy-tel-number-button"
                                                >
                                                    {showCheckTel ? <Checkmark /> : <Replicate onClick={handleCopyToClipboardTel} />}
                                                </Tooltip>
                                            </div>
                                        </div>
                                        <div className={classes.clickables}>
                                            <div className="clickables__mail">
                                                <Email />
                                                <a href={`mailto: ${t('NAVIGATION.CONTACT_MAIL')}`}>{t('NAVIGATION.CONTACT_MAIL')}</a>
                                            </div>
                                            <div className="copy">
                                                <Tooltip
                                                    classes={{
                                                        tooltip: classes.tooltip,
                                                    }}
                                                    title={t('COMMON.COPY_LABEL')}
                                                    placement="right-start"
                                                    TransitionComponent={Fade}
                                                    data-testid="copy-mail-button"
                                                >
                                                    {showCheckMail ? <Checkmark /> : <Replicate onClick={handleCopyToClipboardMail} />}
                                                </Tooltip>
                                            </div>
                                        </div>
                                         <div className={classes.clickables}>
                                            <div className="clickables__chat">
                                                <ChatBot />
                                                <a href="tel:+49 (0) 511 - 95 73 95 4444">{t('NAVIGATION.CONTACT_CHAT')}</a>
                                            </div>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>*/}
                </div>
            </div>
        </>
    );
};

export default Navigation;
